import React, { useState, useEffect } from "react";
import NavBar from "./NavBar";
import ReactProSidebar from "./ReactProSidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCog,
  faLock,
  faKey,
  faSave,
  faCheckCircle,
  faTimesCircle,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import "../../../styles/Account.css";
import { generateHash } from "../../../API/hashUtil";
import api from "../../../API/axiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import CustomAlertDialog from "../../Dialog";
import { useAuth } from "../../../context/AuthContext";
import LoadingScreen from "../../LoadingScreen";

function AdminPanel() {
  const { user, loading, isAuthenticated, logout } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [validations, setValidations] = useState({
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
  });
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState("");
  const [alertConfig, setAlertConfig] = useState({
    open: false,
    title: "",
    message: "",
    iconType: "info",
    buttonText: "OK",
    onConfirm: null,
  });
  // Password visibility states for all 3 fields
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const username = user?.username;

  const passwordRegex =
    /^(?!.*<script>)(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>]).{8,20}$/;

  const closeAlert = () => setAlertConfig((c) => ({ ...c, open: false }));

  // Password visibility toggle functions
  const toggleCurrentPasswordVisibility = () => {
    setShowCurrentPassword(!showCurrentPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateNewPassword = (password) => {
    if (!passwordRegex.test(password)) {
      setErrors(
        "Password must be 8-20 characters, include uppercase, lowercase, number, special character, and not contain '<script>'.",
      );
      return false;
    }
    setErrors("");
    return true;
  };

  const handleResetLink = async (event) => {
    event.preventDefault();

    if (step === 1) {
      const hexHash = generateHash({ username, password: currentPassword });

      try {
        const response = await api.post(
          `/password/checkpassword`,
          {
            username,
            password: currentPassword,
          },
          {
            headers: {
              "X-XSS-Protection": "1; mode=block",

              "X-Content-Type-Options": "nosniff",
              "X-Frame-Options": "DENY",
              "Referrer-Policy": "strict-origin-when-cross-origin",
              "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
            },
            params: {
              HPPcode: hexHash,
            },
          },
        );

        if (response.data === true) {
          setAlertConfig({
            open: true,
            title: "Password validated",
            message: "Proceed to change it.",
            iconType: "info",
            onConfirm: () => {
              setStep(2);
              closeAlert();
            },
          });
        } else {
          setAlertConfig({
            open: true,
            title: "Error",
            message: "Current password is incorrect.",
            iconType: "error",
            onConfirm: closeAlert,
          });
        }
      } catch (error) {
        console.error("Password validation error:", error);
        setAlertConfig({
          open: true,
          title: "Error",
          message: "Current password is incorrect.",
          iconType: "error",
          onConfirm: closeAlert,
        });
      }
    } else if (step === 2) {
      if (newPassword !== confirmPassword) {
        setAlertConfig({
          open: true,
          title: "Error",
          message: "New password and confirmation do not match.",
          iconType: "error",
          onConfirm: closeAlert,
        });
        return;
      }

      if (!validateNewPassword(newPassword)) return;

      const changeHash = generateHash({
        email: username,
        password: newPassword,
      });

      try {
        await api.post(
          `/password/change`,
          {
            email: username,
            password: newPassword,
            HPPcode: changeHash,
          },
          {
            headers: {
              "X-XSS-Protection": "1; mode=block",

              "X-Content-Type-Options": "nosniff",
              "X-Frame-Options": "DENY",
              "Referrer-Policy": "strict-origin-when-cross-origin",
              "Permissions-Policy": "camera=(), microphone=(), geolocation=()",
            },
          },
        );

        setAlertConfig({
          open: true,
          title: "Success",
          message: "Password changed successfully. You need to sign in again.",
          iconType: "info",
          onConfirm: () => {
            localStorage.removeItem("LogIn");
            localStorage.removeItem("LogOut");
            localStorage.removeItem("Token");
            localStorage.removeItem("User_Role");
            localStorage.removeItem("login");
            navigate("/");
          },
        });
      } catch (error) {
        console.error("Password change error:", error);
        setAlertConfig({
          open: true,
          title: "Error",
          message: "Failed to change password.",
          iconType: "error",
          onConfirm: closeAlert,
        });
      }
    }
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);

    setValidations({
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      hasMinLength: password.length >= 8,
    });
  };

  const calculateStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[\W]/.test(password)) strength++;

    if (strength <= 2) return "weak";
    if (strength === 3) return "medium";
    return "strong";
  };

  const passwordStrength = calculateStrength(newPassword);
  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    navigate("/", { replace: true });
    return null;
  }
  return (
    <div className="container">
      <NavBar />
      <CustomAlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        iconType={alertConfig.iconType}
        buttonText={alertConfig.buttonText}
        onConfirm={alertConfig.onConfirm}
        onClose={closeAlert}
      />
      <main className="main-admin-panel">
        <div className="profile-settings">
          <h2>
            <FontAwesomeIcon icon={faUserCog} style={{ fontSize: "20px" }} />
            Account Settings
          </h2>

          <div className="change-password">
            <h4>
              <FontAwesomeIcon icon={faLock} style={{ fontSize: "18px" }} />
              Change Password
            </h4>
            <p>
              You can change your password for security reasons or reset it if
              you forget it. Make sure it is over 8 characters and has at least
              1 uppercase letter, 1 lowercase letter, 1 numeric digit, and 1
              special character.
            </p>

            <form
              className="change-password-form"
              onSubmit={handleResetLink}
              style={{ paddingLeft: "0px", width: "300px" }}
            >
              <div className="password-inputs">
                <div className="input-group">
                  <label>
                    <FontAwesomeIcon
                      icon={faKey}
                      style={{ fontSize: "16px" }}
                    />
                    Current Password
                  </label>
                  <div className="signin__input-wrapper">
                    <input
                      className="signin__input"
                      type={showCurrentPassword ? "text" : "password"}
                      id="current-password"
                      name="currentPassword"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      required
                      style={{
                        width: "300px",
                        paddingRight: "45px",
                        position: "relative",
                      }}
                    />
                    <button
                      type="button"
                      onClick={toggleCurrentPasswordVisibility}
                      style={{
                        position: "absolute",
                        right: "15px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#666",
                        fontSize: "14px",
                        padding: "0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "18px",
                        height: "18px",
                        zIndex: 10,
                      }}
                      aria-label={
                        showCurrentPassword ? "Hide password" : "Show password"
                      }
                    >
                      <FontAwesomeIcon
                        icon={showCurrentPassword ? faEyeSlash : faEye}
                      />
                    </button>
                  </div>
                </div>

                {step === 2 && (
                  <div className="password-container">
                    <div className="password-inputs">
                      <div className="input-group">
                        <label>
                          <FontAwesomeIcon
                            icon={faKey}
                            style={{ fontSize: "16px" }}
                          />
                          New Password
                        </label>
                        <div className="signin__input-wrapper">
                          <input
                            className="signin__input"
                            type={showNewPassword ? "text" : "password"}
                            id="new-password"
                            name="newPassword"
                            value={newPassword}
                            onChange={handlePasswordChange}
                            placeholder="Enter new password"
                            required
                            style={{
                              width: "300px",
                              paddingRight: "45px",
                              position: "relative",
                            }}
                          />
                          <button
                            type="button"
                            onClick={toggleNewPasswordVisibility}
                            style={{
                              position: "absolute",
                              right: "15px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#666",
                              fontSize: "14px",
                              padding: "0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "18px",
                              height: "18px",
                              zIndex: 10,
                            }}
                            aria-label={
                              showNewPassword
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            <FontAwesomeIcon
                              icon={showNewPassword ? faEyeSlash : faEye}
                            />
                          </button>
                        </div>
                      </div>

                      <div className="input-group">
                        <label>
                          <FontAwesomeIcon
                            icon={faKey}
                            style={{ fontSize: "16px" }}
                          />
                          Confirm New Password
                        </label>
                        <div className="signin__input-wrapper">
                          <input
                            className="signin__input"
                            type={showConfirmPassword ? "text" : "password"}
                            id="confirm-password"
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            required
                            style={{
                              width: "300px",
                              paddingRight: "45px",
                              position: "relative",
                            }}
                          />
                          <button
                            type="button"
                            onClick={toggleConfirmPasswordVisibility}
                            style={{
                              position: "absolute",
                              right: "15px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#666",
                              fontSize: "14px",
                              padding: "0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "18px",
                              height: "18px",
                              zIndex: 10,
                            }}
                            aria-label={
                              showConfirmPassword
                                ? "Hide password"
                                : "Show password"
                            }
                          >
                            <FontAwesomeIcon
                              icon={showConfirmPassword ? faEyeSlash : faEye}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="password-feedback-container">
                      <div className="password-strength-bar">
                        <div className={`strength ${passwordStrength}`}></div>
                      </div>
                      <div className="strength-label">
                        {passwordStrength === "weak"
                          ? "Weak"
                          : passwordStrength === "medium"
                            ? "Medium"
                            : "Strong"}
                      </div>

                      <div className="password-requirements">
                        <div
                          className={`requirement ${
                            validations.hasUpperCase ? "met" : "unmet"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              validations.hasUpperCase
                                ? faCheckCircle
                                : faTimesCircle
                            }
                            className="requirement-icon"
                          />
                          <span>Uppercase Letter</span>
                        </div>

                        <div
                          className={`requirement ${
                            validations.hasLowerCase ? "met" : "unmet"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              validations.hasLowerCase
                                ? faCheckCircle
                                : faTimesCircle
                            }
                            className="requirement-icon"
                          />
                          <span>Lowercase Letter</span>
                        </div>

                        <div
                          className={`requirement ${
                            validations.hasNumber ? "met" : "unmet"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              validations.hasNumber
                                ? faCheckCircle
                                : faTimesCircle
                            }
                            className="requirement-icon"
                          />
                          <span>Number</span>
                        </div>

                        <div
                          className={`requirement ${
                            validations.hasSpecialChar ? "met" : "unmet"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              validations.hasSpecialChar
                                ? faCheckCircle
                                : faTimesCircle
                            }
                            className="requirement-icon"
                          />
                          <span>Special Character</span>
                        </div>

                        <div
                          className={`requirement ${
                            validations.hasMinLength ? "met" : "unmet"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={
                              validations.hasMinLength
                                ? faCheckCircle
                                : faTimesCircle
                            }
                            className="requirement-icon"
                          />
                          <span>Minimum 8 Characters</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {errors && (
                  <p style={{ color: "red", fontSize: "12px" }}>{errors}</p>
                )}
              </div>

              <button type="submit" className="save-changes">
                <FontAwesomeIcon icon={faSave} style={{ fontSize: "16px" }} />
                {step === 1 ? "Validate Password" : "Save Changes"}
              </button>
            </form>
          </div>
        </div>
      </main>
      <ReactProSidebar />
    </div>
  );
}

export default AdminPanel;
